import React, { useState, useEffect } from "react";
import { Container, Row, Col, Modal, Button } from "react-bootstrap";
import heroBg from "../assets/images/HeroBanner.png";
import "../styles/AcotCertificationResources.css";
import { useNavigate } from "react-router-dom";

const CertificationResources = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  // Loading for resource list updates (pagination) to avoid flashing the whole page
  const [loadingResources, setLoadingResources] = useState(false);
  const [error, setError] = useState(null);
  const [tokenState, setTokenState] = useState(localStorage.getItem("token"));
  const [showModal, setShowModal] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ totalPages: 1 });
  // const limit = 10; //  Show 6 resources per page
  const [limit, setLimit] = useState(10);
  const navigate = useNavigate();

  // Default icons for visual consistency
  const defaultIcons = [
    <span className="material-symbols-outlined">coronavirus</span>,
    <span className="material-symbols-outlined">school</span>,
    <span className="material-symbols-outlined">biotech</span>,
  ];

  // Watch token changes
  useEffect(() => {
    const interval = setInterval(() => {
      const currentToken = localStorage.getItem("token");
      if (currentToken !== tokenState) {
        setTokenState(currentToken);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [tokenState]);

  //  Fetch resources (with pagination)
  useEffect(() => {
    const fetchAcotResources = async () => {
      try {
        if (resources.length === 0) setLoading(true);
        else setLoadingResources(true);
        const response = await fetch(
          `${process.env.REACT_APP_BASEURL}/getAllAcotResources?page=${currentPage}&limit=${limit}`
        );

        if (!response.ok) throw new Error("Failed to fetch ACOT resources");

        const responseData = await response.json();
        const data = responseData.data || [];

        // Filter only active ones (status === 1)
        const activeResources = data.filter(
          (resource) => resource.status === 1
        );

        // Transform for UI
        const transformedResources = activeResources.map((resource, index) => ({
          title: resource.title,
          description: resource.description,
          icon: defaultIcons[index % defaultIcons.length],
          link: resource.link,
          file_url: resource.file_url,
        }));

        // Prevent pagination glitch when limit changes or total pages shrink
        const newPagination = responseData.pagination || { totalPages: 1 };

        // If current page > total pages (after limit change), reset to last available page
        if (
          currentPage > newPagination.totalPages &&
          newPagination.totalPages > 0
        ) {
          setCurrentPage(newPagination.totalPages);
          return; // Stop here so we don’t flash empty data
        }

        setPagination(newPagination);
        setResources(transformedResources);
        setError(null);
      } catch (err) {
        console.error("Error fetching ACOT resources:", err);
        setError(err.message);
        setResources([]);
      } finally {
        setLoading(false);
        setLoadingResources(false);
      }
    };

    fetchAcotResources();
  }, [currentPage, limit]);

  const handleMemberLogin = () => navigate("/member-login");
  const handleEdcclogin = () => navigate("/edcc-login");

  const handleResourceClick = (resource) => {
    const hasLink = resource.link?.trim();
    const hasFileUrl = resource.file_url?.trim();

    if (hasLink && hasFileUrl) {
      setSelectedResource(resource);
      setShowModal(true);
    } else if (hasLink) {
      openUrl(resource.link);
    } else if (hasFileUrl) {
      openUrl(resource.file_url);
    }
  };

  const openUrl = (url) => {
    if (
      url.startsWith("http://") ||
      url.startsWith("https://") ||
      url.startsWith("www.")
    ) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      window.location.href = url;
    }
  };

  const handleModalChoice = (type) => {
    if (selectedResource) {
      if (type === "link") openUrl(selectedResource.link);
      else if (type === "file") openUrl(selectedResource.file_url);
    }
    setShowModal(false);
    setSelectedResource(null);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages && page !== currentPage) {
      setCurrentPage(page);
    }
  };

  // Loading view
  if (loading) {
    if (loading && resources.length === 0) {
      return (
        <div className="text-center py-5 poppins-regular">
          <p>Loading ACOT resources...</p>
        </div>
      );
    }

    return (
      <>
        <section
          className="hero-fixed"
          style={{ backgroundImage: `url(${heroBg})` }}
        >
          <div className="hero-content">
            <h1>All India Occupational Therapists' Association</h1>
            <div className="hero-buttons">
              <button className="glass-button" onClick={handleMemberLogin}>
                Member Login
              </button>
              <button className="glass-button" onClick={handleEdcclogin}>
                Ergonomic Certificate login
              </button>
            </div>
          </div>
        </section>
        <div className="scrollable-content">
          <Container>
            <h1 className="resources-title poppins-medium">
              Certification Resources under AIOTA
            </h1>
            <p className="text-center">Loading ACOT resources...</p>
          </Container>
        </div>
      </>
    );
  }

  // Error view
  if (error) {
    return (
      <>
        <section
          className="hero-fixed"
          style={{ backgroundImage: `url(${heroBg})` }}
        >
          <div className="hero-content">
            <h1>All India Occupational Therapists' Association</h1>
            <div className="hero-buttons">
              <button className="glass-button" onClick={handleMemberLogin}>
                Member Login
              </button>
              <button className="glass-button" onClick={handleEdcclogin}>
                Ergonomic Certificate login
              </button>
            </div>
          </div>
        </section>
        <div className="scrollable-content">
          <Container>
            <h1 className="resources-title poppins-medium">
              Certification Resources under AIOTA
            </h1>
            <p className="text-center">Error loading resources: {error}</p>
            <div className="text-center">
              <button
                className="btn btn-primary"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          </Container>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Hero */}
      <section
        className="hero-fixed"
        style={{ backgroundImage: `url(${heroBg})` }}
      >
        <div className="hero-scroll-content">
          <div className="hero-content-home poppins-regular">
            <h1 className="hero-heading">
              All India Occupational Therapists' Association
            </h1>
            {!tokenState && (
              <div className="hero-buttons">
                <button className="glass-button" onClick={handleMemberLogin}>
                  Member Login
                </button>
                <button className="glass-button" onClick={handleEdcclogin}>
                  Ergonomic Certificate login
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Resources */}
      <div className="scrollable-content-home">
        <div className="content-container">
          <section className="resources-container">
            <Container>
              <h1 className="resources-title poppins-medium">
                Certification Resources under AIOTA
              </h1>
              <p className="resources-subtitle poppins-semibold">
                The Following Certification Resources available under AIOTA
              </p>

              {Array.from(
                { length: Math.ceil(resources.length / 2) },
                (_, row) => (
                  <Row key={row}>
                    {[0, 1].map((col) => {
                      const index = row * 2 + col;
                      const resource = resources[index];
                      if (!resource) return null;
                      return (
                        <Col key={index} md={6}>
                          <div
                            className="resource-card-link"
                            onClick={() => handleResourceClick(resource)}
                          >
                            <div className="resource-card">
                              <div className="resource-icon">
                                {resource.icon}
                              </div>
                              <div className="resource-content">
                                <h3 className="resource-title poppins-medium">
                                  {resource.title}
                                </h3>
                                <p className="resource-description poppins-regular">
                                  {resource.description}
                                </p>
                              </div>
                              <div className="resource-arrow">
                                <span className="material-symbols-outlined">
                                  arrow_forward_ios
                                </span>
                              </div>
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                )
              )}

              {/*  Pagination Controls */}

              {pagination.totalPages > 1 && (
                <div
                  className="d-flex justify-content-end align-items-center mt-4"
                  style={{ marginRight: "20px" }}
                >
              
                  <div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      disabled={currentPage === 1 || loading}
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      Prev
                    </Button>
                    <span className="mx-2 poppins-regular">
                      Page {currentPage} of {pagination.totalPages}
                    </span>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      disabled={
                        currentPage === pagination.totalPages || loading
                      }
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </Container>
          </section>
        </div>
      </div>

      {/* Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="poppins-medium">
            Choose Resource Type
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="poppins-regular">
          <p>
            This resource has both a link and a file. Which would you like to
            view?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => handleModalChoice("link")}>
            Open Link
          </Button>
          <Button variant="primary" onClick={() => handleModalChoice("file")}>
            View File
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CertificationResources;
